import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from './api/http';
import { useAuth } from './auth/AuthProvider';
import { resolveActiveCustomerContext } from './auth/customerContext';
import CustomerDashboardPreviewPage from './customerDashboardPreview/CustomerDashboardPreviewPage';
import { normalizeMaintStatus } from './customerDashboardPreview/statusTags';

export default function CustomerDashboardPreview({ useDashboardPageShell = false } = {}) {
  const navigate = useNavigate();
  const { session } = useAuth();
  const customerId =
    session?.customerId ?? Number(localStorage.getItem('customerId') || 0);
  const customerName = resolveActiveCustomerContext(session).customerName;
  const accountLabel = customerName || 'Customer account';
  const accountTriggerLabel = 'Account';

  const [forklifts, setForklifts] = useState([]);
  const [maintSummary, setMaintSummary] = useState({
    overdueCount: 0,
    dueSoonCount: 0,
    upcomingCount: 0,
  });
  const [serviceSummary, setServiceSummary] = useState({
    inServiceCount: 0,
    downCount: 0,
    waitingPartsCount: 0,
  });
  const [settings, setSettings] = useState({
    serviceIntervalType: 'DAYS',
    serviceIntervalValue: 90,
    dueSoonWindow: 30,
    upcomingWindow: 60,
  });
  const [subscription, setSubscription] = useState({
    status: 'loading',
    priceId: null,
    currentPeriodEnd: null,
    entitled: false,
    accessState: 'none',
  });
  const [loading, setLoading] = useState(false);
  const [maintFilter, setMaintFilter] = useState('ALL');
  const [serviceFilter, setServiceFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);

  const loadSettings = useCallback(async () => {
    if (!customerId) return;
    try {
      const { data } = await api.get(
        `/api/customers/${customerId}/maintenance-settings`
      );
      setSettings({
        serviceIntervalType: data?.serviceIntervalType || 'DAYS',
        serviceIntervalValue: Number(data?.serviceIntervalValue || 90),
        dueSoonWindow: Number(data?.dueSoonWindow || 30),
        upcomingWindow: Number(data?.upcomingWindow || 60),
      });
    } catch {
      setSettings({
        serviceIntervalType: 'DAYS',
        serviceIntervalValue: 90,
        dueSoonWindow: 30,
        upcomingWindow: 60,
      });
    }
  }, [customerId]);

  const loadForklifts = useCallback(async () => {
    try {
      const { data } = await api.get('/api/forklifts');
      setForklifts(Array.isArray(data) ? data : []);
    } catch {
      setForklifts([]);
    }
  }, []);

  const loadMaintSummary = useCallback(async () => {
    try {
      const { data } = await api.get('/api/forklifts/summary');
      setMaintSummary({
        overdueCount: Number(data?.overdueCount || 0),
        dueSoonCount: Number(data?.dueSoonCount || 0),
        upcomingCount: Number(data?.upcomingCount || 0),
      });
    } catch {
      setMaintSummary({ overdueCount: 0, dueSoonCount: 0, upcomingCount: 0 });
    }
  }, []);

  const loadServiceSummary = useCallback(async () => {
    try {
      const { data } = await api.get('/api/forklifts/service-summary');
      setServiceSummary({
        inServiceCount: Number(data?.inServiceCount || 0),
        downCount: Number(data?.downCount || 0),
        waitingPartsCount: Number(data?.waitingPartsCount || 0),
      });
    } catch {
      setServiceSummary({ inServiceCount: 0, downCount: 0, waitingPartsCount: 0 });
    }
  }, []);

  const loadSubscription = useCallback(async () => {
    try {
      const { data } = await api.get('/api/billing/subscription');
      setSubscription({
        status: data?.status ?? 'none',
        priceId: data?.priceId ?? null,
        currentPeriodEnd: data?.currentPeriodEnd ?? null,
        entitled: Boolean(data?.entitled),
        accessState: data?.accessState ?? 'none',
      });
    } catch {
      setSubscription({
        status: 'error',
        priceId: null,
        currentPeriodEnd: null,
        entitled: false,
        accessState: 'none',
      });
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      loadSettings(),
      loadForklifts(),
      loadMaintSummary(),
      loadServiceSummary(),
      loadSubscription(),
    ]);
    setLoading(false);
  }, [loadSettings, loadForklifts, loadMaintSummary, loadServiceSummary, loadSubscription]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const accessState = String(subscription?.accessState || 'none').toLowerCase();
  const hasEntitlement = Boolean(subscription?.entitled);
  const canManageBilling =
    String(session?.role || '').trim().toLowerCase() === 'customer' &&
    String(session?.customerAccountRole || '').trim().toUpperCase() === 'OWNER' &&
    session?.canManageBilling === true;
  const billableLiftCount = useMemo(
    () => forklifts.filter((f) => String(f.status || '').toUpperCase() !== 'RETIRED').length,
    [forklifts]
  );
  const totalActiveLiftCount = billableLiftCount;

  const nextBillLabel = useMemo(() => {
    if (!subscription?.currentPeriodEnd) return '—';
    const date = new Date(subscription.currentPeriodEnd);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
  }, [subscription?.currentPeriodEnd]);

  const isHours =
    String(settings.serviceIntervalType || 'DAYS').toUpperCase() === 'HOURS';
  const dueSoonWindow = Number(settings.dueSoonWindow || 30);
  const upcomingWindow = Number(settings.upcomingWindow || 60);
  const upcomingLabelEnd = upcomingWindow > dueSoonWindow ? upcomingWindow : dueSoonWindow + 30;
  const unitLabel = isHours ? 'h' : 'd';

  const mobileMaintenanceStatusFilters = [
    {
      key: 'OVERDUE',
      label: 'Overdue',
      value: maintSummary.overdueCount,
      tone: 'danger',
      active: maintFilter === 'OVERDUE',
      onClick: () => setMaintFilter(maintFilter === 'OVERDUE' ? 'ALL' : 'OVERDUE'),
    },
    {
      key: 'DUE_SOON',
      label: 'Due Soon',
      value: maintSummary.dueSoonCount,
      tone: 'warning',
      active: maintFilter === 'DUE_SOON',
      onClick: () => setMaintFilter(maintFilter === 'DUE_SOON' ? 'ALL' : 'DUE_SOON'),
    },
    {
      key: 'UPCOMING',
      label: 'Upcoming',
      value: maintSummary.upcomingCount,
      tone: 'info',
      active: maintFilter === 'UPCOMING',
      onClick: () => setMaintFilter(maintFilter === 'UPCOMING' ? 'ALL' : 'UPCOMING'),
    },
  ];

  const mobileHealthStatusFilters = [
    {
      key: 'DOWN',
      label: 'Down',
      value: serviceSummary.downCount,
      tone: 'danger',
      active: serviceFilter === 'DOWN',
      onClick: () => setServiceFilter(serviceFilter === 'DOWN' ? 'ALL' : 'DOWN'),
    },
    {
      key: 'WAITING_ON_PARTS',
      label: 'Waiting On Parts',
      value: serviceSummary.waitingPartsCount,
      tone: 'warning',
      active: serviceFilter === 'WAITING_ON_PARTS',
      onClick: () =>
        setServiceFilter(serviceFilter === 'WAITING_ON_PARTS' ? 'ALL' : 'WAITING_ON_PARTS'),
    },
    {
      key: 'IN_SERVICE',
      label: 'In Service',
      value: serviceSummary.inServiceCount,
      tone: 'success',
      active: serviceFilter === 'IN_SERVICE',
      onClick: () => setServiceFilter(serviceFilter === 'IN_SERVICE' ? 'ALL' : 'IN_SERVICE'),
    },
  ];

  const filtered = useMemo(() => {
    let list = forklifts.filter(
      (f) => String(f.status || '').toUpperCase() !== 'RETIRED'
    );

    if (maintFilter !== 'ALL') {
      list = list.filter(
        (f) => normalizeMaintStatus(f.maintenanceStatus) === maintFilter
      );
    }

    if (serviceFilter !== 'ALL') {
      list = list.filter(
        (f) => String(f.status || '').toUpperCase() === serviceFilter
      );
    }

    const query = search.trim().toLowerCase();
    if (query) {
      list = list.filter((f) =>
        [f.unitId, f.make, f.model, f.serialNumber].some((value) =>
          String(value || '').toLowerCase().includes(query)
        )
      );
    }

    return list;
  }, [forklifts, maintFilter, search, serviceFilter]);

  const sorted = useMemo(() => {
    const rank = { OVERDUE: 0, DUE_SOON: 1, UPCOMING: 2, ON_TRACK: 3 };
    return [...filtered].sort((a, b) => {
      const aRank = rank[normalizeMaintStatus(a.maintenanceStatus)] ?? 99;
      const bRank = rank[normalizeMaintStatus(b.maintenanceStatus)] ?? 99;
      if (aRank !== bRank) return aRank - bRank;
      return String(a.unitId || '').localeCompare(String(b.unitId || ''), undefined, {
        numeric: true,
      });
    });
  }, [filtered]);

  const alertConfig = useMemo(() => {
    if (accessState === 'canceled_entitled' || String(subscription.status || '').toLowerCase() === 'canceled') {
      return {
        type: 'info',
        message: 'Subscription canceled',
        description: hasEntitlement
          ? `Paid access remains enabled until the current period ends on ${nextBillLabel}.`
          : 'Review your fleet before continuing to Billing to see the options available for your account.',
        action: canManageBilling ? {
          label: 'Review Fleet',
          onClick: () => navigate('/customer/forklifts'),
        } : null,
      };
    }
    if (!hasEntitlement && subscription.status !== 'loading' && subscription.status !== 'error') {
      return {
        type: 'warning',
        message: 'No active subscription',
        description: billableLiftCount < 1
          ? 'Add at least 1 forklift to start a subscription.'
          : 'Subscribe to enable billing and manage your fleet in ManageLift.',
        action: canManageBilling ? {
          label: 'Set up / Review Fleet',
          onClick: () => navigate('/customer/forklifts'),
        } : null,
      };
    }
    if (accessState === 'payment_issue_until_period_end') {
      return {
        type: 'warning',
        message: 'Payment issue',
        description: `Paid access remains enabled through your current period ending on ${nextBillLabel}.`,
        mobileDescription: `— access active through ${nextBillLabel}.`,
      };
    }
    if (accessState === 'payment_issue_grace') {
      return {
        type: 'warning',
        message: 'Payment grace period',
        description: `Your account is in a 7-day grace period after period end (${nextBillLabel}). Paid access will be revoked when grace ends.`,
      };
    }
    return null;
  }, [accessState, billableLiftCount, canManageBilling, hasEntitlement, navigate, nextBillLabel, subscription.status]);

  const clearFilters = () => {
    setMaintFilter('ALL');
    setServiceFilter('ALL');
    setSearch('');
  };

  return (
    <CustomerDashboardPreviewPage
      accountLabel={accountLabel}
      accountMenuOpen={accountMenuOpen}
      accountTriggerLabel={accountTriggerLabel}
      alertConfig={alertConfig}
      clearFilters={clearFilters}
      dueSoonWindow={dueSoonWindow}
      loading={loading}
      maintFilter={maintFilter}
      maintSummary={maintSummary}
      mobileHealthStatusFilters={mobileHealthStatusFilters}
      mobileMaintenanceStatusFilters={mobileMaintenanceStatusFilters}
      navigate={navigate}
      refreshForklifts={loadAll}
      search={search}
      selectedRowId={selectedRowId}
      serviceFilter={serviceFilter}
      serviceSummary={serviceSummary}
      setAccountMenuOpen={setAccountMenuOpen}
      setMaintFilter={setMaintFilter}
      setSearch={setSearch}
      setSelectedRowId={setSelectedRowId}
      setServiceFilter={setServiceFilter}
      sortedForklifts={sorted}
      totalActiveLiftCount={totalActiveLiftCount}
      unitLabel={unitLabel}
      upcomingLabelEnd={upcomingLabelEnd}
      useDashboardPageShell={useDashboardPageShell}
    />
  );
}
